import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { FiHeart, FiAward, FiUsers, FiGlobe } from 'react-icons/fi';

const AboutContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 60vh;
  background: linear-gradient(135deg, #ff6a00, #ee0979, #6a11cb, #2575fc);
  background-size: 300% 300%;
  animation: gradientShift 12s ease infinite;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background-image: url('/assets/about-hero.jpg');
  background-size: cover;
  background-position: center;
  opacity: 0.4;
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  span {
    background: linear-gradient(135deg, #d395ff, #ff7fd9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
`;

const StorySection = styled.section`
  padding: 6rem 10%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  background: white;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    padding: 4rem 5%;
    gap: 3rem;
  }
`;

const StoryContent = styled(motion.div)`
  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 1.5rem;

    span {
      color: var(--primary-color);
      display: block;
      font-size: 1rem;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 0.5rem;
    }
  }

  p {
    color: var(--text-gray);
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    line-height: 1.8;
  }
`;

const StoryImage = styled(motion.div)`
  position: relative;
  height: 500px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(211,149,255,0.2), rgba(255,127,217,0.2));
    z-index: 1;
  }
`;

const StatsSection = styled.section`
  padding: 4rem 10%;
  background: linear-gradient(135deg, #f8f8f8, #ffffff);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 3rem 5%;
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);

  svg {
    font-size: 2.5rem;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-gray);
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.9rem;
  }
`;

const ValuesSection = styled.section`
  padding: 6rem 10%;
  background: var(--dark-bg);
  color: white;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  span {
    color: var(--primary-color);
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    display: block;
    margin-bottom: 1rem;
  }

  h2 {
    font-size: 3rem;
    font-weight: 700;
    color: white;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 1px solid var(--border-dark);
  text-align: center;
  backdrop-filter: blur(10px);

  svg {
    font-size: 2.5rem;
    color: var(--primary-color);
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
    color: white;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    font-size: 0.95rem;
  }
`;

const TimelineSection = styled.section`
  padding: 6rem 10%;
  background: white;
`;

const Timeline = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 4rem auto 0;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 100%;
    background: linear-gradient(180deg, var(--primary-color), var(--secondary-color));

    @media (max-width: 768px) {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: 3rem;
  display: flex;
  justify-content: ${props => props.position === 'left' ? 'flex-end' : 'flex-start'};
  padding-left: ${props => props.position === 'left' ? '0' : '50%'};

  @media (max-width: 768px) {
    padding-left: 60px;
    justify-content: flex-start;
  }
`;

const TimelineContent = styled.div`
  width: 80%;
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    ${props => props.position === 'left' ? 'right: -15px;' : 'left: -15px;'}
    transform: translateY(-50%);
    width: 30px;
    height: 30px;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    border-radius: 50%;
    border: 4px solid white;
    box-shadow: var(--shadow-sm);

    @media (max-width: 768px) {
      left: -45px;
    }
  }

  .year {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
  }

  p {
    color: var(--text-gray);
    line-height: 1.6;
  }
`;

const About = () => {
  const { scrollYProgress } = useScroll();
  const heroRef = useRef(null);
  
  const heroY = useTransform(scrollYProgress, [0, 0.5], ['0%', '30%']);
  const smoothHeroY = useSpring(heroY, { stiffness: 100, damping: 30 });

  const [storyRef, storyInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const stats = [
    { icon: FiHeart, number: '50+', label: 'Unique Flavors' },
    { icon: FiAward, number: '25+', label: 'Awards Won' },
    { icon: FiUsers, number: '10k+', label: 'Happy Customers' },
    { icon: FiGlobe, number: '5+', label: 'Countries' },
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Quality First',
      description: 'We never compromise on ingredients. Only the finest go into our candies.'
    },
    {
      icon: FiAward,
      title: 'Innovation',
      description: 'Constantly pushing boundaries to create unique flavor experiences.'
    },
    {
      icon: FiUsers,
      title: 'Community',
      description: 'Building a sweet community of candy lovers worldwide.'
    },
    {
      icon: FiGlobe,
      title: 'Sustainability',
      description: 'Committed to eco-friendly practices and packaging.'
    }
  ];

  const timeline = [
    {
      year: '1993',
      title: 'The Sweet Beginning',
      description: 'The company initially started in 1993 by Mr. Rajesh Jain and went by the name KOXI.',
      position: 'left'
    },
    {
      year: '2002',
      title: 'Transition to Kemlo',
      description: 'The brand was transitioned to KEMLO and setup in Gwalior, Madhya Pradesh.',
      position: 'right'
    },
    {
      year: '2005',
      title: 'Expanding Products',
      description: 'Won "Best Artisan Candy" award at the National Food Festival.',
      position: 'left'
    },
    {
      year: '2007',
      title: 'Nationwide Reach',
      description: 'Started shipping to states all around the country.',
      position: 'right'
    },
    {
      year: '2012',
      title: 'over 50+ products',
      description: 'Expanded the factory and increased product lineup.',
      position: 'left'
    },
    {
      year: '2020',
      title: 'Started Global Export',
      description: 'Started exporting our bestsellers Globally.',
      position: 'right'
    },
    {
      year: '2023',
      title: 'Setup an Exhibition at AAHAR',
      description: 'We were there at AAHAR and we will be there next time too!.',
      position: 'left'
    }
  ];

  return (
    <AboutContainer>
      <HeroSection ref={heroRef}>
        <HeroBackground style={{ y: smoothHeroY }} />
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle>
            Crafting <span>Sweetness</span>
            <br />
            Since 2002
          </HeroTitle>
          <HeroSubtitle>
            Every piece of Kemlo candy tells a story of passion, 
            innovation, and dedication to creating moments of joy.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <StorySection ref={storyRef}>
        <StoryContent
          initial={{ opacity: 0, x: -30 }}
          animate={storyInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>
            <span>OUR STORY</span>
            From the town of Gwalior to a Global Sensation
          </h2>
          <p>
            KEMLO began its journey in 1993, when Mr. Rajesh Jain founded the company under the name KOXI. What started as a small confectionery initiative soon grew through dedication to quality and innovation. As the business expanded, the company was rebranded as KEMLO in 2002, marking a new phase of growth and modern manufacturing.

Today, KEMLO is a leading confectionery manufacturer based in Gwalior, Madhya Pradesh, serving markets across India. The company integrates production, management, and trading, ensuring consistent quality and efficient operations.
          </p>
          <p>
            Backed by over 50 years of collective industry experience, KEMLO combines advanced production technology, skilled technical expertise, and continuous product innovation to deliver high-quality confectionery products and maintain strong customer satisfaction.
          </p>
        </StoryContent>
        
        <StoryImage
          initial={{ opacity: 0, x: 30 }}
          animate={storyInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <img src="/assets/aahar.png" alt="Our Story" />
        </StoryImage>
      </StorySection>

      <StatsSection ref={statsRef}>
        {stats.map((stat, index) => (
          <StatCard
            key={index}
            initial={{ opacity: 0, y: 30 }}
            animate={statsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5 }}
          >
            <stat.icon />
            <h3>{stat.number}</h3>
            <p>{stat.label}</p>
          </StatCard>
        ))}
      </StatsSection>

      <ValuesSection ref={valuesRef}>
        <SectionTitle>
          <span>WHY CHOOSE US</span>
          <h2>Our Core Values</h2>
        </SectionTitle>

        <ValuesGrid>
          {values.map((value, index) => (
            <ValueCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={valuesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <value.icon />
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </ValueCard>
          ))}
        </ValuesGrid>
      </ValuesSection>

      <TimelineSection>
        <SectionTitle>
          <span>OUR JOURNEY</span>
          <h2>Milestones That Made Us</h2>
        </SectionTitle>

        <Timeline>
          {timeline.map((item, index) => (
            <TimelineItem
              key={index}
              position={item.position}
              initial={{ opacity: 0, x: item.position === 'left' ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <TimelineContent position={item.position}>
                <div className="year">{item.year}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </TimelineSection>
    </AboutContainer>
  );
};

export default About;